import React from 'react';
import Chart from 'chart.js';
import Highcharts from 'highcharts';
import Highstock from 'highcharts/highstock';
import { DownloadPngButton } from '../components/common/pngExport';

/** Slug for PNG file names derived from chart titles. */
export const chartDownloadFileName = (text, fallback = 'chart') => {
  const slug = String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
};

export const DS_CHART_TOKENS = {
  paper: 'var(--paper)',
  gray100: 'var(--gray-100)',
  gray200: 'var(--gray-200)',
  gray300: 'var(--gray-300)',
  gray500: 'var(--gray-500)',
  gray600: 'var(--gray-600)',
  gray700: 'var(--gray-700)',
  ink: 'var(--ink)',
  green100: 'var(--green-100)',
  green300: 'var(--green-300)',
  green400: 'var(--green-400)',
  amber100: 'var(--amber-100)',
  amber300: 'var(--amber-300)',
  amber400: 'var(--amber-400)',
  rose100: 'var(--rose-100)',
  rose300: 'var(--rose-300)',
  rose400: 'var(--rose-400)',
  semBlue100: 'var(--sem-blue-100)',
  semBlue300: 'var(--sem-blue-300)',
  semBlue400: 'var(--sem-blue-400)',
  purple100: 'var(--purple-100)',
  purple300: 'var(--purple-300)',
  purple400: 'var(--purple-400)',
};

export const DS_VIZ_COLORS = [
  DS_CHART_TOKENS.green400,
  DS_CHART_TOKENS.semBlue400,
  DS_CHART_TOKENS.amber400,
  DS_CHART_TOKENS.rose400,
  DS_CHART_TOKENS.purple400,
  DS_CHART_TOKENS.green300,
  DS_CHART_TOKENS.amber300,
  DS_CHART_TOKENS.rose300,
];

export const DS_CHART_COLORS = DS_VIZ_COLORS;

export const DS_REFERENCE_LINE_COLORS = {
  optimal: DS_CHART_TOKENS.green400,
  borderline: DS_CHART_TOKENS.amber400,
  risk: DS_CHART_TOKENS.rose400,
};

export const DS_SEMANTIC_COLORS = {
  rose: DS_CHART_TOKENS.rose400,
  amber: DS_CHART_TOKENS.amber400,
  green: DS_CHART_TOKENS.green400,
  blue: DS_CHART_TOKENS.semBlue400,
  purple: DS_CHART_TOKENS.purple400,
};

export const DS_GAUGE_ZONES = [
  { from: 0, to: 40, color: DS_CHART_TOKENS.green400 },
  { from: 40, to: 70, color: DS_CHART_TOKENS.amber400 },
  { from: 70, to: 100, color: DS_CHART_TOKENS.rose400 },
];

export const DS_HEATMAP_PALETTE = {
  rose: [DS_CHART_TOKENS.rose100, DS_CHART_TOKENS.rose300, DS_CHART_TOKENS.rose400],
  amber: [DS_CHART_TOKENS.amber100, DS_CHART_TOKENS.amber300, DS_CHART_TOKENS.amber400],
  green: [DS_CHART_TOKENS.green100, DS_CHART_TOKENS.green300, DS_CHART_TOKENS.green400],
  blue: [DS_CHART_TOKENS.semBlue100, DS_CHART_TOKENS.semBlue300, DS_CHART_TOKENS.semBlue400],
  purple: [DS_CHART_TOKENS.purple100, DS_CHART_TOKENS.purple300, DS_CHART_TOKENS.purple400],
};

export const DS_WATERFALL_COLORS = {
  positive: DS_CHART_TOKENS.green400,
  negative: DS_CHART_TOKENS.rose400,
  total: DS_CHART_TOKENS.ink,
  subtotal: DS_CHART_TOKENS.gray700,
  connector: 'color-mix(in srgb, var(--ink) 18%, transparent)',
};

export const DS_CORRELATION_COLORS = {
  positive: DS_CHART_TOKENS.rose400,
  negative: DS_CHART_TOKENS.green400,
  diagonal: DS_CHART_TOKENS.ink,
  grid: DS_CHART_TOKENS.gray200,
};

export const DS_SPARKLINE_SIZE = { width: 120, height: 24 };

// Single quotes, not double: these strings get interpolated into HTML
// style="..." attributes (tooltip headerFormat/pointFormat) — double quotes
// break the attribute and corrupt the DOM Highcharts renders.
const FONT_FAMILY = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const MONO_FAMILY = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const GRID_COLOR = DS_CHART_TOKENS.gray200;
const BORDER_COLOR = DS_CHART_TOKENS.gray300;
const AXIS_COLOR = DS_CHART_TOKENS.gray600;
const CATEGORY_COLOR = DS_CHART_TOKENS.gray700;
const INK_COLOR = DS_CHART_TOKENS.ink;
const PAPER_COLOR = DS_CHART_TOKENS.paper;
const EASING = 'cubic-bezier(.22,1,.36,1)';

const COLOR_MAP = {
  '#2563eb': DS_CHART_TOKENS.semBlue400,
  '#2168f3': DS_CHART_TOKENS.semBlue400,
  '#1d4ed8': DS_CHART_TOKENS.semBlue400,
  '#0951e1': DS_CHART_TOKENS.semBlue400,
  '#0066fb': DS_CHART_TOKENS.semBlue400,
  '#60a5fa': DS_CHART_TOKENS.semBlue300,
  '#bfdbfe': DS_CHART_TOKENS.semBlue100,
  '#eff6ff': DS_CHART_TOKENS.semBlue100,
  '#dbeafe': DS_CHART_TOKENS.semBlue100,
  '#1d6fa4': DS_CHART_TOKENS.semBlue400,
  '#5396bd': DS_CHART_TOKENS.semBlue300,
  '#00a04f': DS_CHART_TOKENS.green400,
  '#00b37e': DS_CHART_TOKENS.green400,
  '#35cb7f': DS_CHART_TOKENS.green400,
  '#14866d': DS_CHART_TOKENS.green400,
  '#4fad96': DS_CHART_TOKENS.green300,
  '#a0d4c8': DS_CHART_TOKENS.green300,
  '#e6f3f0': DS_CHART_TOKENS.green100,
  '#dcfce7': DS_CHART_TOKENS.green100,
  '#c54b4f': DS_CHART_TOKENS.rose400,
  '#ff6969': DS_CHART_TOKENS.rose400,
  '#f56565': DS_CHART_TOKENS.rose400,
  '#f17b6e': DS_CHART_TOKENS.rose300,
  '#c43320': DS_CHART_TOKENS.rose400,
  '#c80010': DS_CHART_TOKENS.rose400,
  '#fdedeb': DS_CHART_TOKENS.rose100,
  '#f9bab2': DS_CHART_TOKENS.rose300,
  '#1fbdc0': DS_CHART_TOKENS.green300,
  '#2ec4c7': DS_CHART_TOKENS.green300,
  '#14b8a6': DS_CHART_TOKENS.green300,
  '#68d391': DS_CHART_TOKENS.green300,
  '#5163ba': DS_CHART_TOKENS.purple400,
  '#7c3aed': DS_CHART_TOKENS.purple400,
  '#5b21b6': DS_CHART_TOKENS.purple400,
  '#ddd6fe': DS_CHART_TOKENS.purple100,
  '#ffb500': DS_CHART_TOKENS.amber400,
  '#ff9800': DS_CHART_TOKENS.amber400,
  '#f59e0b': DS_CHART_TOKENS.amber300,
  '#efa030': DS_CHART_TOKENS.amber300,
  '#f6e05e': DS_CHART_TOKENS.amber300,
  '#fbce7a': DS_CHART_TOKENS.amber300,
  '#d97706': DS_CHART_TOKENS.amber400,
  '#b45309': DS_CHART_TOKENS.amber400,
  '#fdf1e4': DS_CHART_TOKENS.amber100,
  '#009fc5': DS_CHART_TOKENS.semBlue400,
  '#777777': DS_CHART_TOKENS.gray600,
  '#d8d8d8': DS_CHART_TOKENS.gray300,
  '#c7cde0': DS_CHART_TOKENS.gray300,
  '#d6e6fc': DS_CHART_TOKENS.gray300,
  '#8895af': DS_CHART_TOKENS.gray500,
  '#9ca3af': DS_CHART_TOKENS.gray500,
  '#6b7280': DS_CHART_TOKENS.gray600,
  '#4b5563': DS_CHART_TOKENS.gray700,
  '#475569': DS_CHART_TOKENS.gray700,
  '#374151': DS_CHART_TOKENS.gray700,
  '#324467': DS_CHART_TOKENS.ink,
  '#0f1115': DS_CHART_TOKENS.ink,
  '#000000': DS_CHART_TOKENS.ink,
  '#ffffff': DS_CHART_TOKENS.paper,
  '#fff': DS_CHART_TOKENS.paper,
  'white': DS_CHART_TOKENS.paper,
  '#a1a1a1': AXIS_COLOR,
  '#e8e8e8': DS_CHART_TOKENS.gray300,
  '#ebebeb': DS_CHART_TOKENS.gray300,
  '#e5e7eb': DS_CHART_TOKENS.gray300,
  '#f3f4f6': DS_CHART_TOKENS.gray200,
  '#f9fafb': DS_CHART_TOKENS.gray100,
  '#b7cfff': DS_CHART_TOKENS.semBlue100,
  '#fbd38d': DS_CHART_TOKENS.amber300,
  '#fbb6ce': DS_CHART_TOKENS.rose300,
  '#e9f2ff': DS_CHART_TOKENS.semBlue100,
};

const buildAreaGradient = (color) => ({
  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
  stops: [
    [0, `color-mix(in srgb, ${color} 18%, transparent)`],
    [1, `color-mix(in srgb, ${color} 2%, transparent)`],
  ],
});

const highchartsTheme = {
  colors: DS_CHART_COLORS,
  chart: {
    backgroundColor: PAPER_COLOR,
    borderWidth: 0,
    spacing: [24, 24, 16, 16],
    style: {
      fontFamily: FONT_FAMILY,
      color: CATEGORY_COLOR,
    },
    animation: {
      duration: 600,
      easing: 'easeOutQuart',
    },
  },
  credits: {
    enabled: false,
  },
  title: {
    align: 'left',
    margin: 20,
    style: {
      color: INK_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: '17px',
      fontWeight: '600',
      letterSpacing: '0',
    },
  },
  subtitle: {
    align: 'left',
    style: {
      color: AXIS_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: '12px',
      letterSpacing: '0',
    },
  },
  xAxis: {
    gridLineWidth: 0,
    lineColor: BORDER_COLOR,
    tickColor: BORDER_COLOR,
    labels: {
      style: {
        color: AXIS_COLOR,
        fontFamily: MONO_FAMILY,
        fontSize: '11px',
      },
    },
    title: {
      style: {
        color: CATEGORY_COLOR,
        fontFamily: FONT_FAMILY,
        fontSize: '11px',
        fontWeight: '600',
      },
    },
  },
  yAxis: {
    gridLineColor: GRID_COLOR,
    gridLineDashStyle: 'Dash',
    lineWidth: 0,
    tickWidth: 0,
    labels: {
      style: {
        color: AXIS_COLOR,
        fontFamily: MONO_FAMILY,
        fontSize: '11px',
      },
    },
    title: {
      style: {
        color: CATEGORY_COLOR,
        fontFamily: FONT_FAMILY,
        fontSize: '11px',
        fontWeight: '600',
      },
    },
  },
  legend: {
    align: 'center',
    verticalAlign: 'bottom',
    itemDistance: 20,
    symbolRadius: 6,
    symbolHeight: 8,
    symbolWidth: 8,
    margin: 16,
    padding: 8,
    itemStyle: {
      color: CATEGORY_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: '12px',
      fontWeight: '500',
    },
    itemHoverStyle: {
      color: INK_COLOR,
    },
  },
  tooltip: {
    backgroundColor: PAPER_COLOR,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderRadius: 6,
    padding: 12,
    useHTML: true,
    shadow: {
      color: 'rgba(15,17,21,.10)',
      offsetX: 0,
      offsetY: 8,
      opacity: 0.14,
      width: 18,
    },
    style: {
      color: INK_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: '12px',
      lineHeight: '18px',
    },
  },
  plotOptions: {
    series: {
      borderColor: PAPER_COLOR,
      animation: {
        duration: 700,
      },
      dataLabels: {
        style: {
          color: CATEGORY_COLOR,
          fontFamily: MONO_FAMILY,
          fontSize: '11px',
          fontWeight: '500',
          textOutline: 'none',
        },
      },
      marker: {
        radius: 4,
        lineColor: PAPER_COLOR,
        lineWidth: 2,
        symbol: 'circle',
        states: {
          hover: {
            radiusPlus: 2,
            lineWidthPlus: 0,
          },
        },
      },
      states: {
        hover: {
          lineWidthPlus: 0,
          halo: {
            size: 8,
            opacity: 0.18,
          },
        },
        inactive: {
          opacity: 0.35,
        },
      },
    },
    line: {
      lineWidth: 2.5,
    },
    spline: {
      lineWidth: 2.5,
    },
    area: {
      lineWidth: 2.5,
      fillOpacity: 0.15,
      trackByArea: true,
    },
    areaspline: {
      lineWidth: 2.5,
      fillOpacity: 0.15,
      trackByArea: true,
    },
    column: {
      borderWidth: 0,
      borderRadius: 4,
      groupPadding: 0.18,
      pointPadding: 0.08,
    },
    bar: {
      borderWidth: 0,
      borderRadius: 4,
      groupPadding: 0.18,
      pointPadding: 0.08,
    },
    pie: {
      borderColor: PAPER_COLOR,
      borderWidth: 2,
      slicedOffset: 6,
      dataLabels: {
        connectorColor: BORDER_COLOR,
        connectorWidth: 1,
        distance: 18,
        style: {
          color: CATEGORY_COLOR,
          fontFamily: FONT_FAMILY,
          fontSize: '12px',
          fontWeight: '500',
          textOutline: 'none',
        },
      },
    },
  },
};

let themeApplied = false;

const chartFunctionKeys = new Set(['formatter', 'pointFormatter', 'positioner']);

const cloneChartValue = (value, seen = new WeakMap()) => {
  if (!value || typeof value !== 'object') return value;
  if (value instanceof Date) return value;
  if (seen.has(value)) return seen.get(value);
  if (Array.isArray(value)) {
    const nextArray = [];
    seen.set(value, nextArray);
    value.forEach((item) => nextArray.push(cloneChartValue(item, seen)));
    return nextArray;
  }
  const nextObject = {};
  seen.set(value, nextObject);
  return Object.keys(value).reduce((next, key) => {
    next[key] = chartFunctionKeys.has(key) ? value[key] : cloneChartValue(value[key], seen);
    return next;
  }, nextObject);
};

const mapChartColor = (color) => {
  if (typeof color !== 'string') return color;
  const normalized = color.trim();
  return COLOR_MAP[normalized.toLowerCase()] || normalized;
};

export const getDsChartColor = mapChartColor;

const CSS_COLOR_FALLBACKS = {
  '--paper': '#FFFFFF',
  '--gray-100': '#F9FAFB',
  '--gray-200': '#F3F4F6',
  '--gray-300': '#E5E7EB',
  '--gray-500': '#9CA3AF',
  '--gray-600': '#6B7280',
  '--gray-700': '#4B5563',
  '--ink': '#0F1115',
  '--green-100': '#E6F3F0',
  '--green-300': '#4FAD96',
  '--green-400': '#14866D',
  '--amber-100': '#FDF1E4',
  '--amber-300': '#EFA030',
  '--amber-400': '#D97706',
  '--rose-100': '#FDEDEB',
  '--rose-300': '#F17B6E',
  '--rose-400': '#E8432C',
  '--sem-blue-100': '#E8F1F6',
  '--sem-blue-300': '#5396BD',
  '--sem-blue-400': '#1D6FA4',
  '--purple-100': '#F2EBFD',
  '--purple-300': '#9F70F1',
  '--purple-400': '#7C3AED',
};

export const resolveCssColorToken = (color) => {
  const mappedColor = mapChartColor(color);
  if (typeof mappedColor !== 'string') return mappedColor;

  const tokenMatch = mappedColor.match(/^var\((--[^),\s]+)(?:,[^)]+)?\)$/);
  if (!tokenMatch) return mappedColor;

  const tokenName = tokenMatch[1];
  if (
    typeof window === 'undefined'
    || typeof document === 'undefined'
    || !window.getComputedStyle
    || !document.documentElement
  ) {
    return CSS_COLOR_FALLBACKS[tokenName] || mappedColor;
  }

  return window.getComputedStyle(document.documentElement).getPropertyValue(tokenName).trim()
    || CSS_COLOR_FALLBACKS[tokenName]
    || mappedColor;
};

export const getCanvasChartColor = (color) => (
  Array.isArray(color) ? color.map(resolveCssColorToken) : resolveCssColorToken(color)
);

export const withDsChartTheme = (ChartComponent) => {
  const DsChartComponent = ({ config, downloadName, disableDownload = false, ...props }) => {
    const nodeRef = React.useRef(null);
    const chart = React.createElement(ChartComponent, {
      ...props,
      config: normalizeHighchartsConfig(config),
    });

    if (disableDownload) return chart;

    return React.createElement(
      'div',
      { ref: nodeRef, className: 'relative' },
      React.createElement(
        'div',
        { className: 'absolute right-1 top-1 z-10', 'data-download-ignore': 'true' },
        React.createElement(DownloadPngButton, {
          nodeRef,
          fileName: downloadName || chartDownloadFileName(config?.title?.text),
        })
      ),
      chart
    );
  };

  return DsChartComponent;
};

const mergeChartStyle = (style = {}, nextStyle = {}) => ({
  ...style,
  ...nextStyle,
});

const normalizeAxis = (axis, axisType) => {
  const baseAxis = axis || {};
  const isXAxis = axisType === 'x';

  return {
    ...baseAxis,
    gridLineWidth: isXAxis ? 0 : (baseAxis.gridLineWidth ?? 1),
    gridLineColor: isXAxis ? baseAxis.gridLineColor : GRID_COLOR,
    gridLineDashStyle: isXAxis ? baseAxis.gridLineDashStyle : 'Dash',
    lineColor: isXAxis ? BORDER_COLOR : 'transparent',
    lineWidth: isXAxis ? 1 : 0,
    tickColor: BORDER_COLOR,
    tickWidth: isXAxis ? 1 : 0,
    tickLength: isXAxis ? 4 : 0,
    labels: {
      ...baseAxis.labels,
      style: mergeChartStyle(baseAxis.labels?.style, {
        color: isXAxis ? (baseAxis.categories ? CATEGORY_COLOR : AXIS_COLOR) : AXIS_COLOR,
        fontFamily: MONO_FAMILY,
        fontSize: '11px',
      }),
    },
    title: {
      ...baseAxis.title,
      style: mergeChartStyle(baseAxis.title?.style, {
        color: CATEGORY_COLOR,
        fontFamily: FONT_FAMILY,
        fontSize: '11px',
        fontWeight: '600',
        letterSpacing: '0.02em',
      }),
    },
  };
};

const normalizeAxisCollection = (axis, axisType) => {
  if (Array.isArray(axis)) return axis.map((item) => normalizeAxis(item, axisType));
  return normalizeAxis(axis, axisType);
};

const AREA_TYPES = new Set(['area', 'areaspline', 'arearange', 'areasplinerange']);

const normalizeSeriesItem = (seriesItem = {}, index = 0) => {
  const resolvedColor = mapChartColor(seriesItem.color) || DS_CHART_COLORS[index % DS_CHART_COLORS.length];
  const next = {
    ...seriesItem,
    color: resolvedColor,
  };

  ['borderColor', 'fillColor', 'lineColor', 'negativeColor', 'upColor'].forEach((key) => {
    if (typeof seriesItem[key] === 'string') next[key] = mapChartColor(seriesItem[key]);
  });

  if (AREA_TYPES.has(seriesItem.type) && !seriesItem.fillColor) {
    next.fillColor = buildAreaGradient(resolvedColor);
  }

  if (Array.isArray(seriesItem.data)) {
    next.data = seriesItem.data.map((point) => {
      if (!point || typeof point !== 'object' || Array.isArray(point)) return point;
      return {
        ...point,
        color: mapChartColor(point.color),
        borderColor: mapChartColor(point.borderColor),
      };
    });
  }

  return next;
};

export const normalizeHighchartsConfig = (config = {}) => {
  const next = cloneChartValue(config);

  next.colors = Array.isArray(next.colors)
    ? next.colors.map(mapChartColor)
    : DS_CHART_COLORS;

  next.chart = {
    ...next.chart,
    backgroundColor: PAPER_COLOR,
    borderWidth: 0,
    spacing: next.chart?.spacing || [24, 24, 16, 16],
    style: mergeChartStyle(next.chart?.style, {
      fontFamily: FONT_FAMILY,
      color: CATEGORY_COLOR,
    }),
  };

  next.credits = {
    ...next.credits,
    enabled: false,
  };

  next.title = {
    ...next.title,
    align: next.title?.align || 'left',
    style: mergeChartStyle(next.title?.style, {
      color: INK_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: '17px',
      fontWeight: '600',
      letterSpacing: '0',
    }),
  };

  next.subtitle = {
    ...next.subtitle,
    align: next.subtitle?.align || 'left',
    style: mergeChartStyle(next.subtitle?.style, {
      color: AXIS_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: '12px',
    }),
  };

  next.xAxis = normalizeAxisCollection(next.xAxis, 'x');
  next.yAxis = normalizeAxisCollection(next.yAxis, 'y');

  next.legend = {
    ...next.legend,
    align: next.legend?.align || 'center',
    verticalAlign: next.legend?.verticalAlign || 'bottom',
    itemDistance: next.legend?.itemDistance ?? 20,
    symbolRadius: next.legend?.symbolRadius ?? 6,
    symbolHeight: next.legend?.symbolHeight ?? 8,
    symbolWidth: next.legend?.symbolWidth ?? 8,
    margin: next.legend?.margin ?? 16,
    itemStyle: mergeChartStyle(next.legend?.itemStyle, {
      color: CATEGORY_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: '12px',
      fontWeight: '500',
    }),
    itemHoverStyle: mergeChartStyle(next.legend?.itemHoverStyle, {
      color: INK_COLOR,
    }),
  };

  next.tooltip = {
    ...next.tooltip,
    backgroundColor: PAPER_COLOR,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderRadius: 6,
    padding: next.tooltip?.padding ?? 12,
    useHTML: next.tooltip?.useHTML ?? true,
    headerFormat: next.tooltip?.headerFormat || `<span style="font-family:${MONO_FAMILY};color:${AXIS_COLOR};font-size:11px">{point.key}</span><br/>`,
    pointFormat: next.tooltip?.pointFormat || `<span style="font-family:${FONT_FAMILY};color:${CATEGORY_COLOR}">{series.name}</span>: <span style="font-family:${MONO_FAMILY};color:${INK_COLOR};font-weight:600">{point.y}</span><br/>`,
    shadow: {
      color: 'rgba(15,17,21,.10)',
      offsetX: 0,
      offsetY: 8,
      opacity: 0.14,
      width: 18,
    },
    style: mergeChartStyle(next.tooltip?.style, {
      color: INK_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: '12px',
      lineHeight: '18px',
    }),
  };

  next.plotOptions = {
    ...next.plotOptions,
    series: {
      ...next.plotOptions?.series,
      borderColor: PAPER_COLOR,
      animation: {
        duration: next.plotOptions?.series?.animation?.duration ?? 700,
      },
      marker: {
        ...next.plotOptions?.series?.marker,
        radius: 4,
        lineColor: PAPER_COLOR,
        lineWidth: 2,
        symbol: next.plotOptions?.series?.marker?.symbol || 'circle',
        states: {
          ...next.plotOptions?.series?.marker?.states,
          hover: {
            radiusPlus: 2,
            lineWidthPlus: 0,
            ...next.plotOptions?.series?.marker?.states?.hover,
          },
        },
      },
      states: {
        ...next.plotOptions?.series?.states,
        hover: {
          lineWidthPlus: 0,
          halo: {
            size: 8,
            opacity: 0.18,
          },
          ...next.plotOptions?.series?.states?.hover,
        },
        inactive: {
          opacity: 0.35,
          ...next.plotOptions?.series?.states?.inactive,
        },
      },
      dataLabels: {
        ...next.plotOptions?.series?.dataLabels,
        style: mergeChartStyle(next.plotOptions?.series?.dataLabels?.style, {
          color: CATEGORY_COLOR,
          fontFamily: MONO_FAMILY,
          fontSize: '11px',
          fontWeight: '500',
          textOutline: 'none',
        }),
      },
    },
    line: {
      ...next.plotOptions?.line,
      lineWidth: 2.5,
    },
    spline: {
      ...next.plotOptions?.spline,
      lineWidth: 2.5,
    },
    area: {
      ...next.plotOptions?.area,
      lineWidth: 2.5,
      fillOpacity: 0.15,
      trackByArea: true,
    },
    areaspline: {
      ...next.plotOptions?.areaspline,
      lineWidth: 2.5,
      fillOpacity: 0.15,
      trackByArea: true,
    },
    column: {
      ...next.plotOptions?.column,
      borderWidth: 0,
      borderRadius: next.plotOptions?.column?.borderRadius ?? 4,
      groupPadding: 0.18,
      pointPadding: 0.08,
    },
    bar: {
      ...next.plotOptions?.bar,
      borderWidth: 0,
      borderRadius: next.plotOptions?.bar?.borderRadius ?? 4,
      groupPadding: 0.18,
      pointPadding: 0.08,
    },
    pie: {
      ...next.plotOptions?.pie,
      borderColor: PAPER_COLOR,
      borderWidth: 2,
      slicedOffset: next.plotOptions?.pie?.slicedOffset ?? 6,
      colors: Array.isArray(next.plotOptions?.pie?.colors)
        ? next.plotOptions.pie.colors.map(mapChartColor)
        : next.plotOptions?.pie?.colors,
      dataLabels: {
        ...next.plotOptions?.pie?.dataLabels,
        connectorColor: next.plotOptions?.pie?.dataLabels?.connectorColor || BORDER_COLOR,
        connectorWidth: next.plotOptions?.pie?.dataLabels?.connectorWidth ?? 1,
        style: mergeChartStyle(next.plotOptions?.pie?.dataLabels?.style, {
          color: CATEGORY_COLOR,
          fontFamily: FONT_FAMILY,
          fontSize: '12px',
          fontWeight: '500',
          textOutline: 'none',
        }),
      },
    },
  };

  if (Array.isArray(next.series)) {
    next.series = next.series.map(normalizeSeriesItem);
  }

  return next;
};

export const buildSparklineConfig = ({ data, color = DS_CHART_COLORS[0], type = 'areaspline' } = {}) => ({
  chart: {
    type,
    backgroundColor: 'transparent',
    margin: [2, 0, 2, 0],
    width: DS_SPARKLINE_SIZE.width,
    height: DS_SPARKLINE_SIZE.height,
    spacing: [0, 0, 0, 0],
  },
  title: { text: '' },
  credits: { enabled: false },
  legend: { enabled: false },
  xAxis: {
    visible: false,
    labels: { enabled: false },
    title: { text: null },
    startOnTick: false,
    endOnTick: false,
    tickPositions: [],
  },
  yAxis: {
    visible: false,
    labels: { enabled: false },
    title: { text: null },
    startOnTick: false,
    endOnTick: false,
    tickPositions: [],
    gridLineWidth: 0,
  },
  tooltip: { enabled: false },
  plotOptions: {
    series: {
      animation: false,
      enableMouseTracking: false,
      marker: { enabled: false },
      states: { hover: { enabled: false } },
    },
  },
  series: [{
    type,
    data,
    color: mapChartColor(color),
    lineWidth: 2,
    fillColor: type === 'areaspline' || type === 'area'
      ? buildAreaGradient(mapChartColor(color))
      : undefined,
  }],
});

export const buildGaugePane = (zones = DS_GAUGE_ZONES) => ({
  startAngle: -120,
  endAngle: 120,
  background: zones.map((zone, idx) => ({
    outerRadius: '110%',
    innerRadius: '88%',
    backgroundColor: zone.color,
    borderWidth: 0,
    shape: 'arc',
    from: zone.from,
    to: zone.to,
  })),
});

export const buildReferenceZones = (zones = [
  { from: 0, to: 40, color: DS_CHART_TOKENS.green100, label: 'Optimal' },
  { from: 40, to: 70, color: DS_CHART_TOKENS.amber100, label: 'Monitor' },
  { from: 70, to: 100, color: DS_CHART_TOKENS.rose100, label: 'Risk' },
]) => zones.map((zone) => ({
  from: zone.from,
  to: zone.to,
  color: zone.color,
  label: zone.label ? {
    text: zone.label,
    align: 'right',
    x: -8,
    style: {
      color: CATEGORY_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: '10px',
      fontWeight: '600',
      textTransform: 'uppercase',
    },
  } : undefined,
}));

export const buildTargetLine = ({ value, label = 'Target', color = DS_CHART_TOKENS.green400 } = {}) => ({
  value,
  color,
  width: 1,
  dashStyle: 'Dash',
  zIndex: 3,
  label: {
    text: label,
    align: 'right',
    x: -8,
    y: -6,
    style: {
      color,
      fontFamily: FONT_FAMILY,
      fontSize: '10px',
      fontWeight: '600',
      textTransform: 'uppercase',
    },
  },
});

export const buildHeatmapColorAxis = (variant = 'green') => ({
  min: 0,
  max: 100,
  stops: (DS_HEATMAP_PALETTE[variant] || DS_HEATMAP_PALETTE.green).map((color, idx, arr) => [
    idx / (arr.length - 1),
    color,
  ]),
  labels: {
    style: {
      color: AXIS_COLOR,
      fontFamily: MONO_FAMILY,
      fontSize: '11px',
    },
  },
});

export const applyChartTheme = () => {
  if (themeApplied) return;
  themeApplied = true;

  Highcharts.setOptions(highchartsTheme);
  Highstock.setOptions(highchartsTheme);

  if (Chart && Chart.defaults && Chart.defaults.global) {
    Chart.defaults.global.defaultFontFamily = FONT_FAMILY;
    Chart.defaults.global.defaultFontColor = getCanvasChartColor(AXIS_COLOR);
    Chart.defaults.global.defaultFontSize = 12;
    Chart.defaults.global.defaultColor = getCanvasChartColor(CATEGORY_COLOR);

    Chart.defaults.global.legend.labels.fontFamily = FONT_FAMILY;
    Chart.defaults.global.legend.labels.fontColor = getCanvasChartColor(CATEGORY_COLOR);
    Chart.defaults.global.legend.labels.fontSize = 12;
    Chart.defaults.global.legend.labels.boxWidth = 10;
    Chart.defaults.global.legend.labels.padding = 16;
    Chart.defaults.global.legend.labels.usePointStyle = true;
    Chart.defaults.global.legend.position = 'bottom';

    Chart.defaults.global.tooltips.backgroundColor = getCanvasChartColor(PAPER_COLOR);
    Chart.defaults.global.tooltips.titleFontFamily = MONO_FAMILY;
    Chart.defaults.global.tooltips.titleFontColor = getCanvasChartColor(AXIS_COLOR);
    Chart.defaults.global.tooltips.titleFontSize = 12;
    Chart.defaults.global.tooltips.titleMarginBottom = 6;
    Chart.defaults.global.tooltips.bodyFontFamily = FONT_FAMILY;
    Chart.defaults.global.tooltips.bodyFontColor = getCanvasChartColor(INK_COLOR);
    Chart.defaults.global.tooltips.bodyFontSize = 12;
    Chart.defaults.global.tooltips.footerFontFamily = MONO_FAMILY;
    Chart.defaults.global.tooltips.footerFontColor = getCanvasChartColor(INK_COLOR);
    Chart.defaults.global.tooltips.cornerRadius = 6;
    Chart.defaults.global.tooltips.xPadding = 12;
    Chart.defaults.global.tooltips.yPadding = 12;
    Chart.defaults.global.tooltips.caretSize = 0;
    Chart.defaults.global.tooltips.displayColors = false;
    Chart.defaults.global.tooltips.borderColor = getCanvasChartColor(BORDER_COLOR);
    Chart.defaults.global.tooltips.borderWidth = 1;

    Chart.defaults.global.elements.line.borderWidth = 2.5;
    Chart.defaults.global.elements.line.tension = 0.35;
    Chart.defaults.global.elements.line.borderCapStyle = 'round';
    Chart.defaults.global.elements.line.borderJoinStyle = 'round';
    Chart.defaults.global.elements.point.radius = 4;
    Chart.defaults.global.elements.point.borderWidth = 2;
    Chart.defaults.global.elements.point.backgroundColor = getCanvasChartColor(PAPER_COLOR);
    Chart.defaults.global.elements.point.hoverRadius = 6;
    Chart.defaults.global.elements.point.hoverBorderWidth = 2;
    Chart.defaults.global.elements.rectangle.borderWidth = 0;
    if (Chart.defaults.global.elements.arc) {
      Chart.defaults.global.elements.arc.borderColor = getCanvasChartColor(PAPER_COLOR);
      Chart.defaults.global.elements.arc.borderWidth = 2;
    }

    Chart.defaults.global.animation = {
      ...(Chart.defaults.global.animation || {}),
      duration: 700,
      easing: 'easeOutQuart',
    };
    Chart.defaults.global.hover = {
      ...(Chart.defaults.global.hover || {}),
      animationDuration: 200,
    };

    if (Chart.defaults.scale && Chart.defaults.scale.ticks) {
      Chart.defaults.scale.ticks.fontFamily = MONO_FAMILY;
      Chart.defaults.scale.ticks.fontColor = getCanvasChartColor(AXIS_COLOR);
      Chart.defaults.scale.ticks.fontSize = 11;
      Chart.defaults.scale.ticks.padding = 8;
    }
    if (Chart.defaults.scale && Chart.defaults.scale.gridLines) {
      Chart.defaults.scale.gridLines.color = getCanvasChartColor(GRID_COLOR);
      Chart.defaults.scale.gridLines.zeroLineColor = getCanvasChartColor(BORDER_COLOR);
      Chart.defaults.scale.gridLines.drawBorder = false;
      Chart.defaults.scale.gridLines.drawTicks = false;
      Chart.defaults.scale.gridLines.borderDash = [3, 3];
    }
  }
};

applyChartTheme();
